/*************************************************************************************************
 * There are TESTs for this file! To run them, see ./packages/earnings-calculator/README.md
 *
 * Rates and formulas defined and described in detail on this spreadsheet emulator
 * https://docs.google.com/spreadsheets/d/1wgUgyY86rX4r0FtUY8cyc6HEUZk5vTuxpQ-4H53Rj04/edit?gid=207767100#gid=207767100
 *
 *************************************************************************************************/

import { currentApyAndInterestRate, formatDollars } from '@fruitful/fruitils';

const CASH_BACK_RATE = 0.02; // Rate applied to card transactions (assuming qualifying conditions met)

// At 5% APY (488 bps), now derived from `currentAnnualAndMonthlyYield`
// const ANNUAL_INTEREST_RATE = 0.0500069026; // Rate of interest earned over a year. This is not exactly 0.05 because the interest rate is set in Synctera at 0.0488.
// const MONTHLY_INTEREST_RATE = 0.0040746738; // Rate of interest earned each month, where compounded over a year would reach 5%

// At 4% APY (392 bps), now derived from `currentAnnualAndMonthlyYield`
// const ANNUAL_INTEREST_RATE = 0.0399762696
// const MONTHLY_INTEREST_RATE = 0.0032718321

const currentAnnualAndMonthlyYield = () => {
    const { interestRate } = currentApyAndInterestRate();
    const interestRateFraction = interestRate / (100 * 100); // basis points (e.g. 488) -> percent (4.88) -> fraction (0.0488)

    const numPeriods = 365;
    const periodicRate = interestRateFraction / numPeriods; // Always use 365 in earnings calculator for simplicity
    const annualPercentageYield = (1 + periodicRate) ** numPeriods - 1; // Stored as a fraction (5% = 0.05)

    // =NOMINAL(apy, 12)
    const equivalentMonthlyInterestRate =
        12 * ((1 + annualPercentageYield) ** (1 / 12) - 1);
    const monthlyPercentageYield = equivalentMonthlyInterestRate / 12;

    return { annualPercentageYield, monthlyPercentageYield };
};

export const calculateAnnualCashBack = (monthlyCardSpending: number) => {
    return asCurrencyNumber(monthlyCardSpending * 12 * CASH_BACK_RATE);
};

export const calculateAnnualInterest = (
    monthlyCardSpending: number,
    monthlySavingsContributions: number,
    startingCash: number,
) => {
    const { annualPercentageYield, monthlyPercentageYield } =
        currentAnnualAndMonthlyYield();

    // Interest accrued over a year on this static amount, using the APY
    const interestFromStartingCash = startingCash * annualPercentageYield;

    // A year's worth of interest accrued from amount spent on card each month before card balance is paid off.
    // Get the monthly amount of interest from the monthly spend using the "rate of interest earned each month"
    const interestFromCardSpending =
        monthlyCardSpending * 12 * monthlyPercentageYield;

    // A year's worth of interest accrued from balance in savings accounts with monthly contributions.
    // Uses formula for accruing interest each month on the increasing balance from monthly contributions and interest.
    const futureValueRate =
        ((1 + monthlyPercentageYield) ** 12 - 1) / monthlyPercentageYield;
    const futureValue = monthlySavingsContributions * futureValueRate;
    const contributedValue = monthlySavingsContributions * 12;
    const interestFromSavingsContributions = futureValue - contributedValue;

    return asCurrencyNumber(
        interestFromStartingCash +
            interestFromCardSpending +
            interestFromSavingsContributions,
    );
};

export const calculateTotalEarnings = (cashBack: number, interest: number) => {
    return asCurrencyNumber(cashBack + interest);
};

export const calculateDefaultAnnualEarnings = (
    monthlyCardSpending: number,
    monthlySavingsContributions: number,
    startingCash: number,
) => {
    const cashBack = calculateAnnualCashBack(monthlyCardSpending);
    const interest = calculateAnnualInterest(
        monthlyCardSpending,
        monthlySavingsContributions,
        startingCash,
    );
    return calculateTotalEarnings(cashBack, interest);
};

export const formatEarnings = (value: number, includeDollarSign = true) => {
    // rounding up because we do not need the precision with these approximate values,
    // and for small values we'd rather show $1 than $0
    const earnings = formatDollars(value, false);
    return includeDollarSign ? earnings : earnings.replace('$', '');
};

export const formatWholeDollarValue = (value: number) => {
    return formatDollars(value, false);
};

export const asCurrencyNumber = (value: number) => {
    return Number(value.toFixed(2));
};

export const roundUpToNext = (value: number, n: number) => {
    return Math.ceil(value / n) * n;
};
